import dayjs from 'dayjs';

import { useMemo, useState } from 'react';

import ChartHeader from 'components/charts/ChartHeader';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import { chartColors } from 'components/charts/chartJS/utils';
import { Divider, DynamicListChartContainer } from 'components/charts/styled/Chart.styled';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { REPORT } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

import * as S from './DeliveryCompany.styled';

const DeliveryCompanyTab = () => (
	<WidgetPageContainer>
		<WidgetWrapper widgetId={'dcPerformance'}>
			<DCPerformanceList />
		</WidgetWrapper>
		<WidgetWrapper widgetId={'deliveryCompDistribution'}>
			<Divider />
			<DeliveryCompDistribution />
		</WidgetWrapper>
	</WidgetPageContainer>
);

const DCPerformanceList = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const driverPerformanceColumns = [
		{
			Header: 'name',
			accessor: 'name',
			width: 100,
			filterType: FilterType.string,
			showOnMobile: true
		},
		{ Header: 'avgPickupTime', accessor: 'Average_Pickup_Time', width: 100, filterType: FilterType.string },
		{ Header: 'avgDeliveryTime', accessor: 'Average_Delivery_Time', width: 100, filterType: FilterType.string },
		{ Header: 'Return_Rate', accessor: 'Return_Rate', width: 100, filterType: FilterType.string },
		{ Header: 'totalCount', accessor: 'totalCount', width: 100, filterType: FilterType.number },
		{ Header: 'share', accessor: 'Share', width: 100, filterType: FilterType.string },
		{
			Header: 'contractType',
			accessor: 'contractType',
			width: 140,
			filterType: FilterType.enumMulti,
			enumName: 'ClientAgreementType',
			renderCell: ListComponents.CLIENT_AGREEMENT_TYPE
		}
	];

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					Header: 'createdAt',
					accessor: 'createdAt',
					filterType: FilterType.date
				},
				action: 'between',
				value: filter
			}
		],
		[filter]
	);

	return (
		<Card>
			<ChartHeader
				title={'dcPerformance'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				titleTooltip="dcPerformanceTooltip"
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.DC_PERFORMANCE}
					className="importExportListing"
					columns={driverPerformanceColumns}
					exportName="dcPerformance"
					showHeader={false}
					showFilters={false}
					fetchUrl={REPORT.DELIVERY_COMPANY.GET_DC_PERFORMANCE}
					showColumnCalculation={false}
					defaultFilters={defaultFilters}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

export const DeliveryCompDistribution = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const afterFetch = (data) => {
		data.sort((a, b) => b.value - a.value);
		const labels = data.map((item) => item.label);
		const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
		return { labels, datasets };
	};
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_DELIVERY_COMPANY_DISTRIBUTION,
		body,
		parseData: afterFetch
	});

	return (
		<S.ChartCard ref={ref}>
			<ChartHeader
				title={'deliveryCompDistribution'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				titleTooltip="deliveryCompDistributionTooltip"
			/>
			<BarChartV2 data={data} loading={loading} horizontal showLegend />
		</S.ChartCard>
	);
};

export default DeliveryCompanyTab;
